import {
  toWSDate,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerPayeeStatus } from "@wingspanhq/payments/dist/interfaces/payerPayee";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces/payerPayeeEngagement";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { usePayeeQuery } from "../../../../../query/payee/queries/usePayeeQuery";
import { usePayeeEngagementByPayeeIdQuery } from "../../../../../query/payeeEngagements/queries/usePayeeEngagementByPayeeIdQuery";
import { useCollaboratorGroupsQuery } from "../../../../../query/payments/queries";
import { WSQueries } from "../../../../../query/WSQuery";
import { FieldView } from "../../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../../shared/components/ResourceHistory";
import { useEngagementDefaultActions } from "../../../../Engagements/hooks/useEngagementDefaultActions";
import { PayeeIntegrationWidget } from "../../../../Payers/components/IntegrationsSection/PayeeIntegrationWidget";
import { RequirementsTable } from "../../../../Requirements/components/RequirementsTable";
import { selectorRequirementsFromEngagement } from "../../../../Requirements/selectors/selectorRequirementsFromEngagement";
import { RequirementContextType } from "../../../../Requirements/types";
import { usePayeeEngagementHistoryResources } from "../../../hooks/usePayeeEngagementHistoryResources";
import { selectorPayeeEngagementNameTmp } from "../../../selectors/selectorPayeeEngagementsTmp";
import { selectorPayerPayeeEngagementName } from "../../../selectors/selectorPayerPayeeEngagementName";
import {
  buildEngagementHistoryPath,
  PAYEES_ROOT_PATH,
  useGoToPayeeDetails
} from "../../../utils";

type EngagementDetailsSidebarProps = RouteComponentProps<{
  engagementId: string;
  payeeId: string;
}>;

export const RoutePayeeEngagementDetails: React.FC<
  EngagementDetailsSidebarProps
> = ({
  match: {
    params: { engagementId, payeeId }
  },
  history
}) => {
  const queryPayee = usePayeeQuery(payeeId);
  const queryEngagement = usePayeeEngagementByPayeeIdQuery(
    payeeId,
    engagementId
  );
  const queryGroups = useCollaboratorGroupsQuery();
  const fetchDefaultActions = useEngagementDefaultActions();
  const isEngagementActive =
    queryEngagement.data?.status !== PayerPayeeEngagementStatus.Inactive;

  const goToPayeeDetails = useGoToPayeeDetails();

  const historyResources = usePayeeEngagementHistoryResources(engagementId);

  return (
    <WSPage>
      <WSQueries queries={{ queryEngagement, queryPayee, queryGroups }}>
        {({ queryEngagementData, queryPayeeData, queryGroupsData }) => {
          const requirements =
            selectorRequirementsFromEngagement(queryEngagementData);

          const engagementName = isEngagementActive
            ? selectorPayerPayeeEngagementName(queryEngagementData, true)
            : selectorPayeeEngagementNameTmp(
                queryEngagementData,
                queryGroupsData,
                true
              );

          const isInactive =
            queryEngagementData.status === PayerPayeeEngagementStatus.Inactive;

          const groupNames = queryGroupsData
            .filter(group =>
              queryEngagementData.collaboratorGroupIds?.includes(
                group.collaboratorGroupId
              )
            )
            .map(group => group.name);

          const names = wsName({
            user: {
              email: queryPayeeData.user?.email as string,
              profile: queryPayeeData.user?.profile
            },
            member: queryPayeeData.member
          });

          return (
            <WSList>
              <WSPageToolbar
                title={engagementName}
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(queryEngagementData.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                fetchActions={
                  queryPayeeData.payerOwnedData.status ===
                  PayerPayeeStatus.Active
                    ? () =>
                        fetchDefaultActions({
                          engagement: queryEngagementData,
                          payee: queryPayeeData
                        })
                    : undefined
                }
                titlePill={
                  queryEngagementData.status ===
                  PayerPayeeEngagementStatus.Inactive
                    ? {
                        theme: "neutral",
                        text: "Archived",
                        icon: false
                      }
                    : undefined
                }
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_ROOT_PATH);
                    }
                  },
                  {
                    label: names.getResolvedName(),
                    onClick: () => {
                      goToPayeeDetails(queryPayeeData.payeeId);
                    }
                  },
                  {
                    label: engagementName || "Engagement"
                  }
                ]}
              />
              <WSGrid>
                <WSGrid.Item span={{ m: "12", l: "8" }}>
                  <WSList gap="L">
                    <WSElement>
                      <WSSectionToolbar title="Engagement information" />
                      <WSPanel>
                        <WSFlexBox gap="XL">
                          <FieldView
                            label="Engagement name"
                            value={engagementName}
                          />
                          {/*
                   <FieldView
                      label="Type"
                      value={getEngagementLabelByType(
                        queryEngagementData.type,
                        {
                          descriptive: true
                        }
                      )}
                    />
                    */}
                        </WSFlexBox>
                        {groupNames.length ? (
                          <FieldView
                            mt="XL"
                            label={`Group${groupNames.length > 1 ? "s" : ""}`}
                            value={groupNames.join(", ")}
                          />
                        ) : null}
                        {/* <FieldView
                  mt="XL"
                  label="Description"
                  value={"TODO:description"}
                /> */}
                      </WSPanel>
                    </WSElement>
                    {requirements.length ? (
                      <WSElement>
                        <WSSectionToolbar title="Engagement requirements" />
                        <WSPanel p="NONE">
                          <RequirementsTable
                            requirements={requirements}
                            contextType={RequirementContextType.Payee}
                            onViewDetails={async (
                              requirement,
                              additionalData
                            ) => {
                              if ("requirementId" in requirement) {
                                history.push(
                                  `/member/payees/${payeeId}/requirement/${requirement.requirementId}?engagementId=${queryEngagementData.payerPayeeEngagementId}`
                                );
                              }
                            }}
                          />
                        </WSPanel>
                      </WSElement>
                    ) : null}
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "12", l: "4" }}>
                  <WSList gap="L">
                    <PayeeIntegrationWidget
                      isInactive={isInactive}
                      payeeId={queryPayeeData.payeeId}
                      engagementId={queryEngagementData.payerPayeeEngagementId}
                      vendorId={
                        queryEngagement.data?.payerOwnedData.integration
                          ?.quickbooks?.vendorId
                      }
                      expenseAccountId={
                        queryEngagement.data?.payerOwnedData.integration
                          ?.quickbooks?.expenseAccountId
                      }
                    />

                    <ResourceHistory
                      resources={historyResources}
                      onViewAll={() => {
                        history.push(
                          buildEngagementHistoryPath(payeeId, engagementId)
                        );
                      }}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>

              {/*
                <WSPanel pt="S">
                  <CollaboratorsGroupsForm
                    isInactive={isInactive}
                    collaboratorId={queryEngagementData.payerPayeeEngagementId}
                    collaboratorGroupIds={
                      queryEngagementData.collaboratorGroupIds
                    }
                  />
                </WSPanel>
                */}
            </WSList>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
